import React, { useEffect, useState } from 'react';
import {
    Breadcrumbs,
    FormControl,
    Grid,
    InputLabel,
    Link as MuiLink,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
} from '@mui/material';
import SendNotification from './SendNotification';
import { SecuredLayout } from '../Layout/SecuredLayout';
import { useAuth, useProvideSnackBar } from '../../hooks';
import { PartnerWorkerDeviceDetails } from '../../@types/employeeData';
import { DefaultMenuItem, MenuItemType, TAB_VIEW } from '../../@types';
import { getEmployeeDevicesByCompany, getWorkplaces } from '../../service/workplace-service';
import { StyledCard } from '../Layout/styles';
import ErrorBoundary from '../../hooks/errorBoundary';
import { DataGridPro, getGridStringOperators, GridColDef, GridRowSelectionModel, huHU } from '@mui/x-data-grid-pro';
import { Link } from 'react-router-dom';
import DepartureBoardIcon from '@mui/icons-material/DepartureBoard';
import LoadingSpinner from '../Common/LoadingSpinner';
import ListContainer from '../Common/ListContainer';
import EmptyListMessage from '../Common/EmptyListMessage';

const columns: GridColDef[] = [
    {
        field: 'workerIdentifier',
        headerName: 'Dolgozó azonosító',
        minWidth: 150,
        sortable: true,
        headerAlign: 'left',
        align: 'left',
        renderHeader: () => <strong>Dolgozó azonosító</strong>,
        filterOperators: getGridStringOperators().filter(({ value }) => ['contains'].includes(value)),
    },
    {
        field: 'workerName',
        headerName: 'Dolgozó neve',
        minWidth: 230,
        flex: 1,
        sortable: true,
        headerAlign: 'left',
        align: 'left',
        renderHeader: () => <strong>Dolgozó neve</strong>,
        filterOperators: getGridStringOperators().filter(({ value }) => ['contains'].includes(value)),
    },
    {
        field: 'operationUnit',
        headerName: 'Üzemegység',
        minWidth: 230,
        sortable: true,
        headerAlign: 'left',
        align: 'left',
        renderHeader: () => <strong>Üzemegység</strong>,
        filterOperators: getGridStringOperators().filter(({ value }) => ['contains'].includes(value)),
    },
    {
        field: 'stop',
        headerName: 'Megálló',
        minWidth: 230,
        sortable: true,
        headerAlign: 'left',
        align: 'left',
        renderHeader: () => <strong>Megálló</strong>,
        filterOperators: getGridStringOperators().filter(({ value }) => ['contains'].includes(value)),
    },
    {
        field: 'language',
        headerName: 'Nyelv',
        minWidth: 120,
        sortable: true,
        headerAlign: 'left',
        align: 'left',
        renderHeader: () => <strong>Nyelv</strong>,
        filterOperators: getGridStringOperators().filter(({ value }) => ['contains'].includes(value)),
    },
];

const CompanyWorkerDetailsPage = () => {
    const { user, hasInsiderRoles, signOut } = useAuth();
    const { showError, showResponseError } = useProvideSnackBar();

    const [workplaces, setWorkplaces] = useState<MenuItemType[]>([DefaultMenuItem]);
    const [loadingWorkplaces, setLoadingWorkplaces] = useState(false);
    const [selectedWorkplace, setSelectedWorkplace] = useState<MenuItemType>(DefaultMenuItem);

    const [employeeDeviceMap, setEmployeeDeviceMap] = useState<Map<string, PartnerWorkerDeviceDetails[]>>(new Map<string, []>());
    const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
    const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);

    useEffect(() => {
        if (!user) {
            return;
        }
        if (hasInsiderRoles) {
            setLoadingWorkplaces(true);
            getWorkplaces(
                user.accessToken || '',
                (response) => showResponseError(response),
                (message) => showError(message),
            )
                .then((data) => data ? setWorkplaces(data) : null)
                .finally(() => setLoadingWorkplaces(false));
            return;
        }
        const workplace = user.workplace || '';
        if (workplace === '') {
            signOut();
        }
        setSelectedWorkplace({ value: workplace, label: '' });
    }, [user]);

    useEffect(() => {
        if (selectedWorkplace === DefaultMenuItem) {
            return;
        }
        setLoadingEmployeeDetails(true);
        getEmployeeDevicesByCompany(
            selectedWorkplace.value,
            user?.accessToken || '',
            (response) => showResponseError(response),
            (message) => showError(message),
        ).then((detailsByCompany: Record<string, PartnerWorkerDeviceDetails[]>) => {
            if (detailsByCompany) {
                updateEmployeeDetails(detailsByCompany);
            }
        }).finally(() => setLoadingEmployeeDetails(false));
    }, [selectedWorkplace]);

    const updateWorkplace = async (e: SelectChangeEvent) => {
        setSelectedWorkplace(workplaces.find(w => w.value === e.target.value) || DefaultMenuItem);
        setEmployeeDeviceMap(new Map());
    };

    const updateEmployeeDetails = (detailsByCompany: Record<string, PartnerWorkerDeviceDetails[]>) => {
        const employeeDetails = hasInsiderRoles ? detailsByCompany[selectedWorkplace.label] : Object.values(detailsByCompany).flat();
        const localEmployeeDeviceMap: Map<string, PartnerWorkerDeviceDetails[]> = new Map<string, PartnerWorkerDeviceDetails[]>();

        if (!employeeDetails) {
            return;
        }

        employeeDetails.forEach((details: PartnerWorkerDeviceDetails) => {
            const key = details.workerId + '_' + details.language.toLowerCase();
            const employeeDevices = localEmployeeDeviceMap.get(key) || [];
            employeeDevices.push(details);
            localEmployeeDeviceMap.set(key, employeeDevices);
        });
        setEmployeeDeviceMap(localEmployeeDeviceMap);
    };

    const resetSelection = () => {
        setRowSelectionModel([]);
    };

    return (
        <SecuredLayout>
            <Grid sx={{ position: 'relative', zIndex: 10, textAlign: 'right' }}>
                {hasInsiderRoles && (
                    <Breadcrumbs aria-label="breadcrumb" sx={{ display: 'inline-block' }}>
                        <MuiLink
                            component={Link}
                            underline="hover"
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', textAlign: 'right' }}
                            px={{ xs: 1, md: 2 }}
                            color="inherit"
                            to="/conductor">
                            Váltás a vezénylő nézetre
                            <DepartureBoardIcon sx={{ ml: 0.5, mb: -0.125 }} fontSize={'small'} />
                        </MuiLink>
                    </Breadcrumbs>
                )}
            </Grid>
            <Grid display={'flex'} flexDirection={'column'} px={{ xs: 1, md: 2 }} pt={1}
                  sx={{ justifyContent: 'center' }}>
                <Grid container display={'flex'} spacing={4} justifyContent={'center'}>
                    <Grid display={'flex'} flexDirection={'column'} item xs={12} md={5} lg={4}>
                        <SendNotification
                            workerTravelDevices={null}
                            devicesByCompany={null}
                            devicesByWorkerLanguageKey={employeeDeviceMap}
                            viewTab={TAB_VIEW.COMPANY_DEVICE}
                            rowSelectionModel={rowSelectionModel}
                            onSendSuccess={resetSelection} />
                        {hasInsiderRoles && (
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel id="workplace-label">Partner</InputLabel>
                                <Select labelId="workplace-label" id="workplace" value={selectedWorkplace.value}
                                        label="Partner"
                                        sx={{ backgroundColor: 'white' }}
                                        onChange={updateWorkplace} variant="outlined">
                                    <MenuItem
                                        key={DefaultMenuItem.value}
                                        value={DefaultMenuItem.value}
                                        sx={{
                                            minHeight: 36,
                                            lineHeight: '1rem',
                                        }}>
                                        {DefaultMenuItem.label}
                                    </MenuItem>
                                    {workplaces.map(w => (
                                        <MenuItem
                                            key={w.value}
                                            value={w.value}
                                            sx={{
                                                minHeight: 36,
                                                lineHeight: '1rem',
                                            }}>
                                            {w.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Grid>
                    {selectedWorkplace !== DefaultMenuItem && (
                        <Grid item xs={12} display={'flex'}>
                            <StyledCard>
                                {loadingEmployeeDetails ? (
                                    <ListContainer>
                                        <LoadingSpinner />
                                    </ListContainer>
                                ) : (
                                    employeeDeviceMap.size > 0 ? (
                                        <Stack sx={{ height: 'calc(100vh - 500px)', mt: 1 }}>
                                            <ErrorBoundary>
                                                <DataGridPro
                                                    density={'compact'}
                                                    columns={columns}
                                                    rows={Array.from(employeeDeviceMap.keys()).map((workerIdLanguage) => {
                                                        const activeDevicesOfCurrentEmployee = employeeDeviceMap.get(workerIdLanguage);
                                                        if (!activeDevicesOfCurrentEmployee) {
                                                            return;
                                                        }
                                                        const firstDevice = activeDevicesOfCurrentEmployee[0];
                                                        return {
                                                            ...firstDevice,
                                                            language: firstDevice.language.toLowerCase(),
                                                            id: workerIdLanguage,
                                                        };
                                                    })}
                                                    initialState={{
                                                        sorting: {
                                                            sortModel: [
                                                                { field: 'workerName', sort: 'asc' },
                                                                { field: 'language', sort: 'asc' },
                                                            ],
                                                        },
                                                    }}
                                                    rowHeight={35}
                                                    checkboxSelection
                                                    hideFooter={true}
                                                    rowSelectionModel={rowSelectionModel}
                                                    onRowSelectionModelChange={(selectedRows) => {
                                                        setRowSelectionModel(selectedRows);
                                                    }}
                                                    localeText={huHU.components.MuiDataGrid.defaultProps.localeText}
                                                    unstable_headerFilters
                                                />
                                            </ErrorBoundary>
                                        </Stack>
                                    ) : (
                                        <ListContainer>
                                            <EmptyListMessage />
                                        </ListContainer>
                                    ))}
                            </StyledCard>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </SecuredLayout>
    );
};

export default CompanyWorkerDetailsPage;